import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

import { LINKS } from '@/lib/constants';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1] as const,
    },
  },
};

export function ImaginerSection() {
  return (
    <section id="imaginer" className="py-24 lg:py-32 px-5 lg:px-12 bg-black scroll-mt-20">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={containerVariants}
        >
          {/* Section Label */}
          <motion.p
            variants={itemVariants}
            className="text-xs font-medium text-white/40 uppercase tracking-[0.05em]"
          >
            Imaginer
          </motion.p>

          {/* Headline */}
          <motion.h2
            variants={itemVariants}
            className="mt-4 text-3xl sm:text-4xl lg:text-[40px] font-semibold leading-[1.2] tracking-[-0.01em] text-white max-w-2xl"
          >
            The Imaginer is our customer—and our partner.
          </motion.h2>

          {/* Description */}
          <motion.p
            variants={itemVariants}
            className="mt-6 text-base text-white/60 leading-relaxed max-w-2xl"
          >
            In Michelangelo, an <span className="text-white font-medium">Imaginer</span> is someone
            who supports the vision, unlocks new experiences, and helps shape what we build next.
          </motion.p>

          {/* Cards Grid */}
          <motion.div variants={containerVariants} className="mt-12 grid md:grid-cols-2 gap-4">
            {/* Why Imaginer */}
            <motion.div
              variants={itemVariants}
              className="glass-card glass-card-hover p-6 glass-card-blur"
              style={{ willChange: 'transform' }}
            >
              <h3 className="text-base font-semibold text-white">Why &quot;Imaginer&quot;?</h3>
              <p className="text-sm text-white/50 mt-1">
                Because imagination is the real source code.
              </p>
              <div className="mt-4 space-y-3 text-sm text-white/40 leading-relaxed">
                <p>
                  While Michelangelo creates your app, we want you to keep your imagination alive,
                  this is the fuel that turns tools into products.
                </p>
                <p>
                  Being an Imaginer means joining the movement early, supporting innovation, and
                  unlocking new experiences as we expand.
                </p>
              </div>
            </motion.div>

            {/* Join the movement */}
            <motion.div
              variants={itemVariants}
              className="glass-card glass-card-hover p-6 flex flex-col glass-card-blur"
              style={{ willChange: 'transform' }}
            >
              <h3 className="text-base font-semibold text-white">Join the movement</h3>
              <p className="text-sm text-white/50 mt-1">
                Connect, build, and shape the future with us.
              </p>
              <p className="text-sm text-white/40 mt-4 leading-relaxed flex-1">
                Discord is where we share updates and collect feedback.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <a
                  href={LINKS.discord}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white border border-white/20 hover:bg-white/5 transition-all duration-200"
                >
                  Join Discord
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="mailto:sardo@michelangelo.land"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white transition-colors duration-200"
                >
                  Email
                </a>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
